import type { User } from 'firebase/auth';
import { getFirebaseConfig, useEmulators } from '../firebaseConfig';

// Access decision for the game page:
//  'open'      - Firebase not configured, game is public (dev/no-backend)
//  'ok'        - signed in AND on the designers allowlist
//  'login'     - not signed in (caller redirects to /studio to log in)
//  'forbidden' - signed in but not allowlisted
export type GameAccess = 'open' | 'ok' | 'login' | 'forbidden';

// Mirrors the studio gate (signed in + designers/{email} exists). Firebase is
// imported dynamically so it stays out of the main chunk and is skipped entirely
// when the project is not configured.
export async function checkGameAccess(): Promise<GameAccess> {
  const config = getFirebaseConfig();
  if (!config) return 'open';

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAuth, onAuthStateChanged, connectAuthEmulator } = await import('firebase/auth');
    const { getFirestore, doc, getDoc, connectFirestoreEmulator } = await import(
      'firebase/firestore/lite'
    );

    const app = getApps()[0] ?? initializeApp(config);
    const auth = getAuth(app);
    const db = getFirestore(app);
    if (useEmulators()) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
    }

    const user = await new Promise<User | null>((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => {
        unsub();
        resolve(u);
      });
    });
    if (!user?.email) return 'login';

    const snap = await getDoc(doc(db, 'designers', user.email));
    return snap.exists() ? 'ok' : 'forbidden';
  } catch (err) {
    // Can't confirm access - send the visitor to the login screen rather than
    // silently opening the game.
    console.warn('[gate] game access check failed', err);
    return 'login';
  }
}
